import { cva, type VariantProps } from 'class-variance-authority';
import { Download, Play, Star, Trash2 } from 'lucide-react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { cn } from '@/lib/utils';
import { getCoverForWorld } from '@/lib/covers';
import { getLibraryCover } from '@/lib/worldAssets';
import type { WorldSlug } from '@/lib/worlds';
import { STRINGS } from '@/strings/strings';
import { StoryStatusPill } from '@/components/patterns/StoryStatusPill';
import type { PdfStatus, StoryAudioStatus } from '@/features/story/types';

/**
 * Shared story-row pattern used by:
 *   - Start-Page "Deine letzten Geschichten" block (Batch 7) —
 *     density='start': horizontal row with a 75×60 cover tile left,
 *     title + meta stack right. Sönkes `mystories-card-item` frame
 *     (node 8:5553).
 *   - Library grid (Batch 8) — density='library': 40×40 cover tile
 *     left, title bold + 200-char preview, action row at the bottom
 *     (Lesen / Audio / PDF / Delete + Favoriten-Star top-right). Per
 *     Briefing Ch 3 line 627-642.
 *
 * `onClick` makes the OUTER element interactive (whole-card tap).
 * The library-density action icons (audio/pdf/delete/favorite) have
 * their OWN handlers and stop event propagation so the outer onClick
 * doesn't fire on icon taps. Briefing line 643-648.
 */

const storyCardVariants = cva(
  'flex w-full overflow-hidden rounded-lg bg-blue-bg text-left transition-colors',
  {
    variants: {
      density: {
        start: 'items-center',
        // Library uses a column layout on the inner content stack
        // because the action row sits below title + preview + meta.
        library: 'items-stretch',
      },
      interactive: {
        true: 'hover:bg-blue-bg/70 focus-visible:outline-2 focus-visible:outline-dream-navy/50',
        false: '',
      },
    },
    defaultVariants: { density: 'start', interactive: false },
  },
);

type StoryCardVariants = VariantProps<typeof storyCardVariants>;

export interface StoryCardProps extends Omit<StoryCardVariants, 'interactive'> {
  density: 'start' | 'library';
  title: string;
  childName: string | null;
  /** Human-readable world name ("Abenteuer Alltag"), for display only. */
  worldName: string;
  /** WorldSlug — used to resolve the cover-art path via `getCoverForWorld`. */
  worldSlug: WorldSlug;
  /** Pre-formatted DD.MM.YYYY date string — caller owns formatting. */
  createdDate: string;
  onClick?: () => void;
  className?: string;
  /** Optional data-testid pass-through for test queries. */
  testId?: string;

  // ── Library-density-only fields (ignored when density='start') ──
  /** Truncated 200-char body preview from backend. Null hides the line. */
  previewText?: string | null;
  isFavorite?: boolean;
  /** Visibility of the Audio-icon. Briefing line 639. */
  audioReady?: boolean;
  /** Visibility of the PDF-icon. Briefing line 640. */
  pdfReady?: boolean;
  /**
   * Bug #3 Part 1 (2026-05-12): asset-status fields drive the
   * Library-Indicator StoryStatusPill. When `'failed'`, the pill
   * surfaces above the title. Library-density only — ignored when
   * density='start'. Optional to keep call-sites that don't have the
   * field (Start-Page "letzte Geschichten" block) backward-compatible.
   */
  audioStatus?: StoryAudioStatus;
  pdfStatus?: PdfStatus;
  /**
   * Bug #3 Part 2 (2026-05-13): true if a refund has been issued for
   * this story. Renders the `credit_refunded` pill alongside any
   * failed-asset pills. Library-density only.
   */
  refunded?: boolean;
  /**
   * Library action handlers. When omitted, the corresponding icons
   * still render (provided their gate is true) but tapping is a no-op.
   * Tests should always provide these handlers; production call-sites
   * MUST when density='library' for the route to be reachable.
   */
  onToggleFavorite?: () => void;
  onAudioClick?: () => void;
  onPdfClick?: () => void;
  onDeleteClick?: () => void;
}

export function StoryCard({
  density,
  title,
  childName,
  worldName,
  worldSlug,
  createdDate,
  onClick,
  className,
  testId,
  previewText,
  isFavorite,
  audioReady,
  pdfReady,
  audioStatus,
  pdfStatus,
  refunded,
  onToggleFavorite,
  onAudioClick,
  onPdfClick,
  onDeleteClick,
}: StoryCardProps) {
  // Density-specific covers (Batch 8 Library bug-fix commit, 2026-04-26):
  //   - library → per-world `Library_XX.svg` via getLibraryCover
  //   - start   → shared `cover-default.svg` (per-world variant TBD;
  //               getCoverForWorld currently returns the default)
  const coverSrc = density === 'library' ? getLibraryCover(worldSlug) : getCoverForWorld(worldSlug);
  const interactive = Boolean(onClick);

  if (density === 'library') {
    return (
      <LibraryDensityBody
        title={title}
        childName={childName}
        worldName={worldName}
        coverSrc={coverSrc}
        createdDate={createdDate}
        previewText={previewText ?? null}
        isFavorite={Boolean(isFavorite)}
        audioReady={Boolean(audioReady)}
        pdfReady={Boolean(pdfReady)}
        audioStatus={audioStatus}
        pdfStatus={pdfStatus}
        refunded={Boolean(refunded)}
        onClick={onClick}
        onToggleFavorite={onToggleFavorite}
        onAudioClick={onAudioClick}
        onPdfClick={onPdfClick}
        onDeleteClick={onDeleteClick}
        className={className}
        testId={testId}
      />
    );
  }

  // density === 'start' — preserve Batch 7 layout exactly.
  const body = (
    <>
      <div className="h-[60px] w-[75px] shrink-0 overflow-hidden">
        <img src={coverSrc} alt="" className="h-full w-full object-cover" draggable={false} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2 p-3 text-dream-navy">
        <p className="truncate text-xs font-medium leading-3" title={title}>
          {title}
        </p>
        <div className="flex items-start justify-between gap-2 leading-3 whitespace-nowrap">
          {childName && <p className="shrink-0 text-xs opacity-50">{childName}</p>}
          <div className="flex shrink-0 items-center gap-1 text-xxs opacity-25">
            <span>{worldName}</span>
            {createdDate && <span>{createdDate}</span>}
          </div>
        </div>
      </div>
    </>
  );

  if (interactive) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(storyCardVariants({ density: 'start', interactive: true }), className)}
        data-testid={testId}
      >
        {body}
      </button>
    );
  }
  return (
    <div
      className={cn(storyCardVariants({ density: 'start', interactive: false }), className)}
      data-testid={testId}
    >
      {body}
    </div>
  );
}

// ── Library-density implementation ──────────────────────────────

interface LibraryDensityBodyProps {
  title: string;
  childName: string | null;
  worldName: string;
  coverSrc: string;
  createdDate: string;
  previewText: string | null;
  isFavorite: boolean;
  audioReady: boolean;
  pdfReady: boolean;
  audioStatus: StoryAudioStatus | undefined;
  pdfStatus: PdfStatus | undefined;
  refunded: boolean;
  onClick: (() => void) | undefined;
  onToggleFavorite: (() => void) | undefined;
  onAudioClick: (() => void) | undefined;
  onPdfClick: (() => void) | undefined;
  onDeleteClick: (() => void) | undefined;
  className: string | undefined;
  testId: string | undefined;
}

function LibraryDensityBody({
  title,
  childName,
  worldName,
  coverSrc,
  createdDate,
  previewText,
  isFavorite,
  audioReady,
  pdfReady,
  audioStatus,
  pdfStatus,
  refunded,
  onClick,
  onToggleFavorite,
  onAudioClick,
  onPdfClick,
  onDeleteClick,
  className,
  testId,
}: LibraryDensityBodyProps) {
  const S = STRINGS.library.storyCard;

  // Whole-card tap → onClick (navigation); inner action handlers
  // stopPropagation to keep their interaction local.
  const handleCardKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  const stopAndCall = (handler?: () => void) => (e: MouseEvent) => {
    e.stopPropagation();
    handler?.();
  };

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={handleCardKeyDown}
      className={cn(
        storyCardVariants({ density: 'library', interactive: Boolean(onClick) }),
        'flex-row',
        className,
      )}
      data-testid={testId}
    >
      <div className="h-[40px] w-[40px] shrink-0 overflow-hidden self-start mt-3 ml-3">
        <img src={coverSrc} alt="" className="h-full w-full object-cover" draggable={false} />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5 p-3 text-dream-navy">
        {/* Top row: meta (date · child · world) on the left, fav-star top-right */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1 text-xxs text-dream-navy/60 leading-tight">
            {createdDate && <span>{createdDate}</span>}
            {childName && <span>·</span>}
            {childName && <span>{childName}</span>}
            <span>·</span>
            <span>{worldName}</span>
          </div>
          <button
            type="button"
            onClick={stopAndCall(onToggleFavorite)}
            aria-label={S.favoriteAriaLabel}
            aria-pressed={isFavorite}
            className="shrink-0 -mt-0.5 -mr-0.5 p-1 rounded transition-colors hover:bg-dream-navy/5"
            data-testid={testId ? `${testId}-favorite` : undefined}
          >
            <Star
              className={cn(
                'h-4 w-4',
                isFavorite ? 'fill-dream-gold text-dream-gold' : 'text-dream-navy/40',
              )}
              strokeWidth={2}
            />
          </button>
        </div>

        <p className="text-sm font-semibold leading-tight line-clamp-3" title={title}>
          {title}
        </p>

        {(audioStatus === 'failed' || pdfStatus === 'failed' || refunded) && (
          <div className="flex flex-wrap gap-1">
            {audioStatus === 'failed' && <StoryStatusPill type="audio_failed" />}
            {pdfStatus === 'failed' && <StoryStatusPill type="pdf_failed" />}
            {refunded && <StoryStatusPill type="credit_refunded" />}
          </div>
        )}

        {previewText && (
          <p className="text-xs text-dream-navy/70 line-clamp-2 leading-snug">{previewText}</p>
        )}

        <div className="flex items-center gap-3 mt-1.5">
          <span className="text-xs font-medium text-dream-navy underline">{S.readAction}</span>
          {audioReady && (
            <button
              type="button"
              onClick={stopAndCall(onAudioClick)}
              aria-label={S.audioAriaLabel}
              className="p-1 rounded text-dream-navy hover:bg-dream-navy/5 transition-colors"
              data-testid={testId ? `${testId}-audio` : undefined}
            >
              <Play className="h-4 w-4" strokeWidth={2} />
            </button>
          )}
          {pdfReady && (
            <button
              type="button"
              onClick={stopAndCall(onPdfClick)}
              aria-label={S.pdfAriaLabel}
              className="p-1 rounded text-dream-navy hover:bg-dream-navy/5 transition-colors"
              data-testid={testId ? `${testId}-pdf` : undefined}
            >
              <Download className="h-4 w-4" strokeWidth={2} />
            </button>
          )}
          <span className="ml-auto" />
          <button
            type="button"
            onClick={stopAndCall(onDeleteClick)}
            aria-label={S.deleteAriaLabel}
            className="p-1 rounded text-error hover:bg-error/10 transition-colors"
            data-testid={testId ? `${testId}-delete` : undefined}
          >
            <Trash2 className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}
